/**
 * Call Centre AI assist in the console (Stage 6): which helpers this admin can use, and how AI failures are read.
 *
 * The availability is read once (60 s fresh). Every AI action is hidden unless its flag is true. The API answers
 * 503 `ai_unavailable` while a helper is switched off (hide it and read the availability again) and 503 `ai_failed`
 * when the AI service failed or timed out (say so, show it inline too, and carry on). An older API without the AI
 * routes counts as "all off". Nothing the AI suggests is applied or saved until the admin accepts it.
 */

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CALL_CENTRE_ERROR_CODES, useCallCentreAdminService, type CallCentreAiAvailability } from '@actschurch/ccms-sdk';
import { useCallCentrePermissions } from '@/hooks/callcentre/useCallCentrePermissions';
import { callCentreErrorCode, callCentreKeys } from './callCentreUtils';

export const CALL_CENTRE_AI_OFF: CallCentreAiAvailability = {
  callAssist: false,
  careFlag: false,
  scriptAssist: false,
  importAssist: false,
  insights: false,
};

/** The sentence for an AI failure or time-out. */
export const CALL_CENTRE_AI_FAILED_TEXT = 'AI help is not available right now. Carry on as usual.';

/** The console page where each AI helper's flag is switched on (AIManagement feature flags). */
export const AI_FEATURE_FLAGS_PATH = '/admin/ai/feature-flags';

/** GET admin/ai/availability (console roles only); all false while off, on an older API or on any error. */
export function useCallCentreAiAvailability(): CallCentreAiAvailability {
  const admin = useCallCentreAdminService();
  const perms = useCallCentrePermissions();
  const query = useQuery({
    queryKey: callCentreKeys.aiAvailability(),
    queryFn: () => admin.getAiAvailability(),
    enabled: perms.hasConsoleRole,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  if (!perms.hasConsoleRole || !query.data) return CALL_CENTRE_AI_OFF;
  return { ...CALL_CENTRE_AI_OFF, ...query.data };
}

/** After `ai_unavailable`: hide the AI actions at once and read the availability again. */
export function useCallCentreAiUnavailable(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.setQueryData<CallCentreAiAvailability>(callCentreKeys.aiAvailability(), { ...CALL_CENTRE_AI_OFF });
    void queryClient.invalidateQueries({ queryKey: callCentreKeys.aiAvailability() });
  }, [queryClient]);
}

/** `unavailable`: the helper is switched off (hide it). `failed`: anything else (say so and carry on). */
export function callCentreAiErrorKind(err: unknown): 'unavailable' | 'failed' {
  return callCentreErrorCode(err) === CALL_CENTRE_ERROR_CODES.AiUnavailable ? 'unavailable' : 'failed';
}
